import { TextFieldModule } from '@angular/cdk/text-field';
import { DatePipe, DOCUMENT, NgClass, NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostBinding,
    Inject,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { FuseScrollbarDirective } from '@fuse/directives/scrollbar';
import { Client, IMessage, ReconnectionTimeMode } from '@stomp/stompjs';
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { environment } from '../../../../environment/environment';
import { AuthService } from '../../../core/auth/auth.service';
import { UserService } from '../../../core/user/user.service';
import { QuickChatService } from './quick-chat.service';
import { QuickChatCallComponent, QuickChatCallSession } from './quick-chat-call/quick-chat-call.component';
import { CallTrace, Chat, Conversation, UserSearchResult } from './quick-chat.types';

@Component({
    selector: 'quick-chat',
    templateUrl: './quick-chat.component.html',
    styleUrls: ['./quick-chat.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        NgClass,
        MatIconModule,
        MatButtonModule,
        FuseScrollbarDirective,
        NgTemplateOutlet,
        MatFormFieldModule,
        MatInputModule,
        TextFieldModule,
        DatePipe,
        FormsModule,
        QuickChatCallComponent,
    ],
    exportAs: 'quickChat',
})
export class QuickChatComponent implements OnInit, OnDestroy {
    @ViewChild('messageInput') messageInput!: ElementRef<HTMLTextAreaElement>;
    @ViewChild('messageContainer') messageContainer!: ElementRef;
    @ViewChild('attachmentInput') attachmentInput!: ElementRef<HTMLInputElement>;

    conversations: Conversation[] = [];
    chat: Chat[] = [];
    selectedChat: Conversation | null = null;
    opened = false;
    isTyping = false;
    private typingTimeout: any;
    attachmentUploading = false;
    pendingAttachment: {
        fileKey: string;
        fileName: string;
        contentType: string;
        fileSize: number;
    } | null = null;

    onlineUsers: Set<string> = new Set();
    typingUserId: number | null = null;
    typingConversationIds: Set<number> = new Set();
    private remoteTypingTimeouts = new Map<number, any>();
    private outgoingTypingTimeout: any;

    searchQuery = '';
    searchResults: UserSearchResult[] = [];
    showSearchResults = false;
    private searchSubject = new Subject<string>();
    callSession: QuickChatCallSession | null = null;

    private stompClient: Client | null = null;
    protected currentUserId = Number(this.authService.getAuthUserIdFromToken());
    private _unsubscribeAll = new Subject<void>();

    constructor(
        @Inject(DOCUMENT) private document: Document,
        private quickChatService: QuickChatService,
        private authService: AuthService,
        private userService: UserService,
        private cdr: ChangeDetectorRef
    ) {
        this.initSearch();
    }

    @HostBinding('class') get classList() {
        return { 'quick-chat-opened': this.opened };
    }

    ngOnInit(): void {
        // Le chat est un service optionnel : s'il est arrêté, on échoue silencieusement.
        this.quickChatService.getChats().subscribe({
            next: (chats) => {
                this.conversations = this.sortConversations(chats);
            },
            error: () => {
                this.conversations = [];
            },
        });

        this.loadInitialActiveUsers();
        this.initWebSocket();
    }

    private normalizeMessage(message: any): Chat {
        const sentAtRaw = message.sentAtEpochMs ?? (message.sentAt ? Date.parse(message.sentAt) : Date.now());
        const sentAtMs = Number.isFinite(Number(sentAtRaw)) ? Number(sentAtRaw) : Date.now();

        return {
            ...message,
            isMine: message.senderId === this.currentUserId,
            sentAtEpochMs: sentAtMs,
            sentAt: new Date(sentAtMs),
            attachmentFileKey: message.attachmentFileKey || undefined,
            attachmentFileName: message.attachmentFileName || undefined,
            attachmentContentType: message.attachmentContentType || undefined,
            attachmentFileSize: message.attachmentFileSize || undefined,
        } as Chat;
    }

    private initSearch(): void {
        this.searchSubject
            .pipe(
                debounceTime(300),
                distinctUntilChanged(),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((query) => {
                if (query.trim().length < 2) {
                    this.searchResults = [];
                    this.showSearchResults = false;
                    return;
                }

                this.userService.searchUsers(query).subscribe({
                    next: (results) => {
                        this.searchResults = results;
                        this.showSearchResults = true;
                        this.cdr.detectChanges();
                    },
                    error: (err) => console.error('Erreur recherche', err),
                });
            });
    }

    onSearchInput(event: any): void {
        this.searchQuery = event.target.value;
        this.searchSubject.next(this.searchQuery);
    }

    onSearchFocus(): void {
        if (this.searchQuery.trim().length >= 2) {
            this.showSearchResults = true;
        }
    }

    onSearchBlur(): void {
        setTimeout(() => {
            this.showSearchResults = false;
        }, 220);
    }

    selectUserFromSearch(user: UserSearchResult): void {
        this.showSearchResults = false;
        this.searchQuery = '';

        const existing = this.conversations.find((c) => c.user1 === user.id || c.user2 === user.id);
        if (existing) {
            this.selectChat(existing);
        } else {
            this.createNewConversation(user);
        }
    }

    private createNewConversation(user: UserSearchResult): void {
        this.quickChatService.createConversation(user.id).subscribe({
            next: (newConv: Conversation) => {
                this.conversations.unshift(newConv);
                this.selectChat(newConv);
                this.cdr.detectChanges();
            },
            error: (err) => {
                console.error('Erreur creation conversation', err);
            },
        });
    }

    selectChat(conversation: Conversation): void {
        this.selectedChat = conversation;
        this.opened = true;
        this.clearConversationUnread(conversation);

        this.quickChatService.getChatById(conversation.id!).subscribe({
            next: (messages) => {
                this.chat = messages.map((m: any) => this.normalizeMessage(m));
                this.markSelectedConversationAsRead();
                this.scrollToBottom();
            },
            error: () => {
                this.chat = [];
            },
        });
    }

    sendMessage(): void {
        if (!this.messageInput || !this.selectedChat?.id || !this.stompClient) {
            return;
        }

        const content = this.messageInput.nativeElement.value.trim();
        if (!content && !this.pendingAttachment) {
            return;
        }

        if (this.attachmentUploading) {
            return;
        }

        const sentAtMs = Date.now();
        const messageDto = {
            conversationId: Number(this.selectedChat.id),
            senderId: this.currentUserId,
            receiverId: this.getOtherUserId(),
            content,
            messageType: this.pendingAttachment ? 'ATTACHMENT' : 'TEXT',
            sentAtEpochMs: sentAtMs,
            attachmentFileKey: this.pendingAttachment?.fileKey,
            attachmentFileName: this.pendingAttachment?.fileName,
            attachmentContentType: this.pendingAttachment?.contentType,
            attachmentFileSize: this.pendingAttachment?.fileSize,
        };

        this.stompClient.publish({
            destination: '/app/chat.send',
            body: JSON.stringify(messageDto),
        });

        const localMessage = {
            senderId: this.currentUserId,
            isMine: true,
            content,
            sentAt: new Date(sentAtMs),
            sentAtEpochMs: sentAtMs,
            attachmentFileKey: this.pendingAttachment?.fileKey,
            attachmentFileName: this.pendingAttachment?.fileName,
            attachmentContentType: this.pendingAttachment?.contentType,
            attachmentFileSize: this.pendingAttachment?.fileSize,
        } as Chat;

        this.chat.push(localMessage);
        this.updateConversationFromMessage(localMessage, false);

        this.messageInput.nativeElement.value = '';
        this.clearPendingAttachment();
        this.publishTyping(false);
        this.scrollToBottom();
    }

    openAttachmentPicker(): void {
        this.attachmentInput?.nativeElement.click();
    }

    onAttachmentSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        input.value = '';

        if (!file) {
            return;
        }

        if (!this.selectedChat?.id) {
            input.value = '';
            return;
        }

        this.attachmentUploading = true;
        this.quickChatService.uploadAttachment(file, Number(this.selectedChat.id)).subscribe({
            next: (response) => {
                const fileKey = response?.[0]?.key;
                this.attachmentUploading = false;

                if (!fileKey) {
                    return;
                }

                this.pendingAttachment = {
                    fileKey,
                    fileName: file.name,
                    contentType: file.type || 'application/octet-stream',
                    fileSize: file.size,
                };
                this.cdr.detectChanges();
            },
            error: (error) => {
                this.attachmentUploading = false;
                console.error('Erreur upload piece jointe', error);
            },
        });
    }

    removePendingAttachment(): void {
        this.clearPendingAttachment();
    }

    private initWebSocket(): void {
        const token = this.authService.token;
        this.stompClient = new Client({
            brokerURL: environment.WS,
            // Service de chat indisponible : on réessaie avec un délai croissant
            // (5 s, 10 s, 20 s… plafonné à 5 min) au lieu de saturer la console.
            reconnectDelay: 5000,
            reconnectTimeMode: ReconnectionTimeMode.EXPONENTIAL,
            maxReconnectDelay: 5 * 60 * 1000,
            connectHeaders: {
                userId: this.authService.getAuthUserIdFromToken(),
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
        });

        this.stompClient.onConnect = () => {
            this.stompClient!.subscribe(`/topic/chat/${this.currentUserId}`, (message: IMessage) => {
                this.handleIncomingMessage(JSON.parse(message.body));
            });

            this.stompClient!.subscribe(`/topic/typing/${this.currentUserId}`, (message: IMessage) => {
                this.handleTypingEvent(JSON.parse(message.body));
            });

            this.stompClient!.subscribe('/topic/active-users', (message: IMessage) => {
                const activeIds: string[] = JSON.parse(message.body);
                this.onlineUsers = new Set(activeIds);
                this.cdr.detectChanges();
            });
        };

        this.stompClient.activate();
    }

    private handleIncomingMessage(msg: any): void {
        const conversationId = Number(msg.conversationId);
        const existingConversation = this.conversations.find((conv) => Number(conv.id) === conversationId);
        if (!existingConversation) {
            this.quickChatService.getChats().subscribe((chats) => {
                this.conversations = this.sortConversations(chats);
                if (this.selectedChat && conversationId === Number(this.selectedChat.id)) {
                    const newMsg = this.normalizeMessage(msg);
                    this.chat.push(newMsg);
                    this.markSelectedConversationAsRead();
                    this.scrollToBottom();
                }
            });
            return;
        }

        const newMsg = this.normalizeMessage(msg);
        const isOpenConversation = this.selectedChat && conversationId === Number(this.selectedChat.id);
        this.updateConversationFromMessage(newMsg, !isOpenConversation);

        if (isOpenConversation) {
            this.chat.push(newMsg);
            this.markSelectedConversationAsRead();
            this.scrollToBottom();
        }
    }

    private handleTypingEvent(event: any): void {
        if (event.senderId === this.currentUserId) {
            return;
        }

        const conversationId = Number(event.conversationId);
        if (event.typing) {
            this.typingConversationIds.add(conversationId);
            clearTimeout(this.remoteTypingTimeouts.get(conversationId));
            this.remoteTypingTimeouts.set(
                conversationId,
                setTimeout(() => {
                    this.typingConversationIds.delete(conversationId);
                    this.refreshSelectedTypingState();
                    this.cdr.detectChanges();
                }, 3000)
            );
        } else {
            this.typingConversationIds.delete(conversationId);
            clearTimeout(this.remoteTypingTimeouts.get(conversationId));
        }

        this.typingUserId = event.senderId;
        this.refreshSelectedTypingState();
        this.cdr.detectChanges();
    }

    private refreshSelectedTypingState(): void {
        const selectedId = this.selectedChat?.id ? Number(this.selectedChat.id) : null;
        this.isTyping = selectedId !== null && this.typingConversationIds.has(selectedId);
        if (!this.isTyping) {
            this.typingUserId = null;
        }
    }

    isOnline(userId: number): boolean {
        return this.onlineUsers.has(userId.toString());
    }

    private getOtherUserId(): number {
        if (!this.selectedChat) return 0;
        return this.selectedChat.user1 === this.currentUserId ? this.selectedChat.user2 : this.selectedChat.user1;
    }

    getOtherUserFullName(): string {
        if (!this.selectedChat) return '';
        return this.selectedChat.user1 === this.currentUserId
            ? this.selectedChat.user2FullName
            : this.selectedChat.user1FullName;
    }

    getTypingUserName(): string {
        return this.getOtherUserFullName();
    }

    startAudioCall(): void {
        this.openCallPopup('audio');
    }

    startVideoCall(): void {
        this.openCallPopup('video');
    }

    closeCallPopup(): void {
        this.callSession = null;
    }

    onCallFinished(trace: CallTrace): void {
        const durationLabel = this.formatCallDuration(trace.durationSeconds);
        const callLabel = trace.mediaType === 'video' ? 'Appel video' : 'Appel vocal';
        const statusLabel =
            trace.status === 'rejected'
                ? 'refuse'
                : trace.status === 'missed'
                    ? 'manque'
                    : trace.status === 'canceled'
                        ? 'annule'
                        : 'termine';

        const content = `${callLabel} ${statusLabel} • ${durationLabel}`;
        const sentAtMs = trace.endedAtEpochMs;
        const payload = {
            conversationId: trace.conversationId,
            senderId: this.currentUserId,
            receiverId: trace.peerId,
            content,
            messageType: 'CALL_TRACE',
            sentAtEpochMs: sentAtMs,
        };

        if (this.stompClient) {
            this.stompClient.publish({
                destination: '/app/chat.send',
                body: JSON.stringify(payload),
            });
        }

        const localTraceMessage: Chat = {
            conversationId: trace.conversationId,
            senderId: this.currentUserId,
            content,
            messageType: 'CALL_TRACE',
            sentAt: new Date(sentAtMs),
            sentAtEpochMs: sentAtMs,
            isMine: true,
        };

        this.chat.push(localTraceMessage);
        this.updateConversationFromMessage(localTraceMessage, false);
        this.scrollToBottom();
        this.cdr.detectChanges();
    }

    get totalUnreadCount(): number {
        return this.conversations.reduce((total, conv) => total + Number(conv.unreadCount || 0), 0);
    }

    isConversationTyping(conversation: Conversation): boolean {
        return conversation.id != null && this.typingConversationIds.has(Number(conversation.id));
    }

    getConversationUnreadCount(conversation: Conversation): number {
        return Number((conversation as Conversation & { unreadCount?: number }).unreadCount || 0);
    }

    openAttachment(fileKey: string): void {
        const previewWindow = window.open('', '_blank');
        this.quickChatService.downloadAttachment(fileKey).subscribe({
            next: (blob) => {
                const url = window.URL.createObjectURL(blob);
                if (previewWindow) {
                    previewWindow.location.href = url;
                } else {
                    window.open(url, '_blank', 'noopener');
                }
                setTimeout(() => window.URL.revokeObjectURL(url), 60000);
            },
            error: (error) => {
                previewWindow?.close();
                console.error('Erreur ouverture piece jointe', error);
            },
        });
    }

    private scrollToBottom(): void {
        setTimeout(() => {
            if (this.messageContainer) {
                this.messageContainer.nativeElement.scrollTop = this.messageContainer.nativeElement.scrollHeight;
            }
        }, 100);
    }

    trackByFn(index: number, item: any): any {
        return item?.id ?? index;
    }

    trackConversationByFn(index: number, conversation: Conversation): string {
        return `${conversation.id ?? 'conversation'}-${conversation.user1}-${conversation.user2}-${index}`;
    }

    trackMessageByFn(index: number, message: Chat): string {
        const key = message.id ?? message.sentAtEpochMs ?? message.sentAt?.getTime?.() ?? 'message';
        return `${key}-${message.senderId ?? 'sender'}-${index}`;
    }

    toggle(): void {
        this.opened = !this.opened;
        if (this.opened) {
            this.selectedChat = null;
            this.chat = [];
        } else {
            this.clearPendingAttachment();
        }
    }

    private openCallPopup(mediaType: 'audio' | 'video'): void {
        if (!this.selectedChat?.id) {
            return;
        }

        this.callSession = {
            conversationId: Number(this.selectedChat.id),
            peerId: this.getOtherUserId(),
            contactName: this.getOtherUserFullName(),
            mediaType,
        };
    }

    onTyping(): void {
        if (!this.selectedChat || !this.stompClient) return;

        this.publishTyping(true);

        clearTimeout(this.outgoingTypingTimeout);
        this.outgoingTypingTimeout = setTimeout(() => {
            this.publishTyping(false);
        }, 2000);
    }

    private publishTyping(typing: boolean): void {
        if (!this.selectedChat || !this.stompClient) return;

        const typingEvent = {
            senderId: this.currentUserId,
            receiverId: this.getOtherUserId(),
            conversationId: Number(this.selectedChat.id),
            typing,
        };

        this.stompClient.publish({
            destination: '/app/chat.typing',
            body: JSON.stringify(typingEvent),
        });
    }

    private loadInitialActiveUsers(): void {
        this.quickChatService.getUsersConnect().subscribe({
            next: (users) => {
                this.onlineUsers = new Set(users);
                this.cdr.detectChanges();
            },
            // Service de chat indisponible : pas d'erreur console, la liste reste vide.
            error: () => {},
        });
    }

    private clearPendingAttachment(): void {
        this.pendingAttachment = null;
        if (this.attachmentInput?.nativeElement) {
            this.attachmentInput.nativeElement.value = '';
        }
    }

    private markSelectedConversationAsRead(): void {
        if (!this.selectedChat?.id) {
            return;
        }

        this.clearConversationUnread(this.selectedChat);
        this.quickChatService.markConversationAsRead(this.selectedChat.id).subscribe({
            error: (err) => console.error('Erreur lecture conversation', err),
        });
    }

    private clearConversationUnread(conversation: Conversation): void {
        this.conversations = this.conversations.map((conv) =>
            Number(conv.id) === Number(conversation.id)
                ? { ...conv, unreadCount: 0 }
                : conv
        );
        if (this.selectedChat && Number(this.selectedChat.id) === Number(conversation.id)) {
            this.selectedChat = { ...this.selectedChat, unreadCount: 0 };
        }
    }

    private updateConversationFromMessage(message: Chat, incrementUnread: boolean): void {
        const conversationId = Number(message.conversationId || this.selectedChat?.id);
        this.conversations = this.sortConversations(
            this.conversations.map((conv) => {
                if (Number(conv.id) !== conversationId) {
                    return conv;
                }

                const unreadCount = incrementUnread ? Number(conv.unreadCount || 0) + 1 : Number(conv.unreadCount || 0);
                return {
                    ...conv,
                    unreadCount,
                    lastMessagePreview: this.getMessagePreview(message),
                    lastMessageType: message.messageType,
                    lastMessageAtEpochMs: message.sentAtEpochMs,
                    lastActivityEpochMs: message.sentAtEpochMs,
                };
            })
        );
    }

    private getMessagePreview(message: Chat): string {
        if (message.messageType === 'CALL_TRACE') {
            return message.content || "Historique d'appel";
        }
        if (message.content) {
            return message.content.length > 80 ? `${message.content.substring(0, 80)}...` : message.content;
        }
        if (message.attachmentFileName) {
            return `Piece jointe: ${message.attachmentFileName}`;
        }
        return 'Nouveau message';
    }

    private sortConversations(conversations: Conversation[]): Conversation[] {
        return [...conversations].sort((a, b) => {
            const aTime = Number(a.lastMessageAtEpochMs || a.lastActivityEpochMs || 0);
            const bTime = Number(b.lastMessageAtEpochMs || b.lastActivityEpochMs || 0);
            return bTime - aTime;
        });
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
        clearTimeout(this.outgoingTypingTimeout);
        this.remoteTypingTimeouts.forEach((timeout) => clearTimeout(timeout));

        if (this.stompClient) {
            this.stompClient.deactivate();
        }
    }

    private formatCallDuration(durationSeconds: number): string {
        const safeDuration = Math.max(0, Math.floor(durationSeconds || 0));
        const minutes = Math.floor(safeDuration / 60);
        const seconds = safeDuration % 60;

        if (minutes > 0) {
            return `${minutes} min ${seconds.toString().padStart(2, '0')} s`;
        }

        return `${seconds} s`;
    }
}
